/*
 * The kept size at the bottom of every page (snippets/size-bar.liquid).
 *
 * Reads what pdp-size-finder.js keeps in localStorage and shows it only when it is a size she
 * can buy. Add to cart goes through the product form when she is on that product's page with
 * that size chosen, so the theme's own cart notification and the _size_source property behave
 * exactly as with the page's button; anywhere else it adds through /cart/add and opens the same
 * notification. On the product page the bar steps aside while the page's own Add to cart is on
 * screen, so there is never a second button next to the first.
 */
class SizeBar extends HTMLElement {
  connectedCallback() {
    this.imageEl = this.querySelector('[data-size-bar-image]');
    this.sizeEl = this.querySelector('[data-size-bar-size]');
    this.noteEl = this.querySelector('[data-size-bar-note]');
    this.addEl = this.querySelector('[data-size-bar-add]');
    this.labelEl = this.querySelector('[data-size-bar-label]');
    this.errorEl = this.querySelector('[data-size-bar-error]');
    this.toastEl = document.querySelector('[data-size-toast]');
    this.addLabel = this.labelEl ? this.labelEl.textContent : 'Add to cart';

    this.addEl.addEventListener('click', () => this.add());
    window.addEventListener('symmetrista:size-saved', (event) => {
      this.render(event.detail && event.detail.saved);
      if (event.detail && event.detail.toast) this.toast();
    });
    /* Another tab kept a different size. */
    window.addEventListener('storage', (event) => {
      if (event.key === SizeBar.STORAGE_KEY) this.render(SizeBar.read());
    });

    this.watchPageButton();
    this.render(SizeBar.read());
  }

  static read() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(SizeBar.STORAGE_KEY) || 'null');
      if (!saved || saved.v !== 1 || !saved.variant_id || !saved.size) return null;
      return saved;
    } catch (error) {
      return null;
    }
  }

  render(saved) {
    this.saved = saved && saved.available && saved.variant_id ? saved : null;
    if (!this.saved) {
      this.update();
      return;
    }
    const side = this.saved.side === 'L' ? 'Left' : 'Right';
    this.sizeEl.textContent = this.saved.size;
    this.noteEl.textContent = [side + ' side smaller', this.saved.us_size].filter(Boolean).join(' · ');
    if (this.imageEl && this.saved.image) {
      this.imageEl.src = this.saved.image;
      this.imageEl.alt = this.saved.title || '';
      this.imageEl.hidden = false;
    }
    const price = this.price(this.saved);
    this.addLabelFull = price ? `${this.addLabel} · ${price}` : this.addLabel;
    this.labelEl.textContent = this.addLabelFull;
    this.errorEl.hidden = true;
    this.update();
  }

  /* "€95" from cents, in the currency the page showed when the size was kept. */
  price(saved) {
    if (typeof saved.price !== 'number' || !saved.currency) return '';
    try {
      const amount = saved.price / 100;
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: saved.currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2
      }).format(amount);
    } catch (error) {
      return '';
    }
  }

  get pageBuy() {
    return document.querySelector('.pdp-buy--gated');
  }

  /* On the product page: is the page's own Add to cart showing, and is it on screen? */
  watchPageButton() {
    const buy = this.pageBuy;
    if (!buy) return;
    this.pageButtonVisible = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        this.pageButtonVisible = entries.some((entry) => entry.isIntersecting);
        this.update();
      }).observe(buy);
    }
    new MutationObserver(() => this.update()).observe(buy, { attributes: true, attributeFilter: ['class'] });
  }

  update() {
    const buy = this.pageBuy;
    const covered = Boolean(buy && buy.classList.contains('is-ready') && this.pageButtonVisible);
    const show = Boolean(this.saved) && !covered;
    this.hidden = !show;
    document.body.classList.toggle('has-size-bar', show);
  }

  toast() {
    if (!this.toastEl) return;
    this.toastEl.hidden = false;
    this.toastEl.classList.add('is-shown');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastEl.classList.remove('is-shown');
      this.toastEl.hidden = true;
    }, 2600);
  }

  /* The page's own form, if it is set to this very size. */
  get pageForm() {
    const buy = this.pageBuy;
    const form = buy && buy.querySelector('form[data-type="add-to-cart-form"], form');
    const id = form && form.querySelector('input[name="id"]');
    return id && String(id.value) === String(this.saved.variant_id) ? form : null;
  }

  add() {
    if (!this.saved || this.adding) return;
    const form = this.pageForm;
    if (form) {
      const button = form.querySelector('[name="add"]');
      if (button && !button.disabled) {
        button.click();
        return;
      }
    }

    this.adding = true;
    this.addEl.setAttribute('aria-disabled', 'true');
    this.errorEl.hidden = true;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = new FormData();
    body.append('id', this.saved.variant_id);
    body.append('quantity', '1');
    body.append('properties[_size_source]', this.saved.sister ? 'finder_sister' : 'finder');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append('sections', cart.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
    }

    const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(url, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message || 'Could not add to cart');
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(state);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch((error) => {
        /* Sold out since it was kept, most likely: send her back to the finder. */
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
      })
      .finally(() => {
        this.adding = false;
        this.addEl.removeAttribute('aria-disabled');
      });
  }
}

SizeBar.STORAGE_KEY = 'symmetrista_size';

customElements.define('size-bar', SizeBar);
